import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import TrustBar from "@/pages/home/components/TrustBar";
import AIAnswerBlock from "@/pages/home/components/AIAnswerBlock";
import ProcessSection from "@/pages/home/components/ProcessSection";
import WhyChooseUs from "@/pages/home/components/WhyChooseUs";
import ServiceAreas from "@/pages/home/components/ServiceAreas";
import TestimonialsSection from "@/pages/home/components/TestimonialsSection";
import FAQSection from "@/pages/home/components/FAQSection";
import FinalCTA from "@/pages/home/components/FinalCTA";
import MidPageCTA from "@/pages/home/components/MidPageCTA";
import { aboutFaqs } from "@/mocks/faqs";
import { Link } from "react-router-dom";

const aboutQA = [
  {
    q: "Who is Knight In Shining Armor Heating & Air?",
    a: "Knight In Shining Armor Heating & Air (KISA) is a licensed HVAC service provider based in McKinney, TX — specializing in air conditioning repair, installation, and maintenance across Dallas and the surrounding metro area. The company is built on technical excellence, honest service, and long-term system performance.",
  },
  {
    q: "What is the mission of KISA Cooling Dallas?",
    a: "Our mission is to deliver reliable, high-performance cooling solutions that keep homes and businesses comfortable, even in the extreme Texas climate. We combine certified expertise with transparent service and a genuine commitment to long-term results for every customer we serve.",
  },
];

const values = [
  { icon: "ri-award-line", title: "Technical Excellence", desc: "Certified technicians trained across all major HVAC brands, system types, and installation configurations for every job.", accent: "from-[#0EA5E9]/12 to-[#0EA5E9]/4" },
  { icon: "ri-shake-hands-line", title: "Honest Service", desc: "Transparent diagnostics, fair pricing, and recommendations you can trust — no upselling, no shortcuts, ever.", accent: "from-emerald-500/10 to-emerald-500/3" },
  { icon: "ri-time-line", title: "Rapid Response", desc: "Same-day service for most calls with emergency availability when your system fails at the worst possible time.", accent: "from-amber-500/10 to-amber-500/3" },
  { icon: "ri-leaf-line", title: "Efficiency Focus", desc: "We prioritize high-efficiency systems to reduce energy costs, extend system lifespan, and lower your carbon footprint.", accent: "from-teal-500/10 to-teal-500/3" },
];

const stats = [
  { value: "Licensed", sub: "& Insured in TX", icon: "ri-shield-check-fill" },
  { value: "Same-Day", sub: "Service Available", icon: "ri-flashlight-line" },
  { value: "All Makes", sub: "& Models Serviced", icon: "ri-tools-line" },
  { value: "DFW Metro", sub: "Coverage Area", icon: "ri-map-pin-2-fill" },
];

export default function AboutPage() {
  return (
    <main className="bg-[#020B14]">
      <Header />

      {/* Hero */}
      <section className="relative w-full min-h-[620px] flex items-center pt-20 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/3b5ef4518f3dceed8b964b91be54be63.jpg"
          alt="About Knight In Shining Armor Heating & Air — KISA Dallas team"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020B14]/94 via-[#020B14]/78 to-[#020B14]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020B14]/40 via-transparent to-[#020B14]/70" />
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0EA5E9]/50 via-[#38bdf8]/20 to-transparent" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-[640px]">
            <nav className="flex items-center gap-2 mb-7" aria-label="breadcrumb">
              <Link to="/" className="font-['Inter'] text-white/40 text-xs hover:text-white/65 transition-colors duration-300">Home</Link>
              <i className="ri-arrow-right-s-line text-white/22 text-xs"></i>
              <span className="font-['Inter'] text-[#7DD3FC] text-xs">About</span>
            </nav>
            <div className="inline-flex items-center gap-2 border border-[#0EA5E9]/35 bg-[#0EA5E9]/8 rounded-full px-4 py-2 mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#38bdf8] animate-pulse flex-shrink-0" />
              <span className="font-['Inter'] text-[#7DD3FC] text-xs font-medium tracking-[0.15em] uppercase whitespace-nowrap">
                McKinney, TX &nbsp;·&nbsp; Serving the DFW Metro
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-white text-4xl md:text-5xl lg:text-[3.4rem] font-bold leading-[1.1] mb-6">
              About Knight In Shining Armor Heating &amp; Air
            </h1>
            <p className="font-['Inter'] text-white/65 text-lg md:text-xl leading-[1.78]">
              A performance-driven HVAC company focused on reliability, efficiency, and long-term comfort for Dallas homes and businesses.
            </p>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* Brand Story — asymmetric layout */}
      <section className="bg-[#050F1A] py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

            {/* Left — Copy */}
            <div>
              <p className="font-['Inter'] text-[#7DD3FC] text-xs font-semibold uppercase tracking-[0.18em] mb-4">Our Story</p>
              <h2 className="font-['Playfair_Display'] text-white text-4xl md:text-[2.9rem] font-bold mb-8 leading-[1.12]">
                Precision Cooling.<br />Built for Dallas.
              </h2>

              {/* Pull quote */}
              <blockquote className="border-l-2 border-[#0EA5E9] pl-6 mb-8">
                <p className="font-['Playfair_Display'] text-white/80 text-xl italic leading-[1.65]">
                  &ldquo;Get it right, get it done, and make sure it lasts. No shortcuts. Just honest, expert HVAC work.&rdquo;
                </p>
              </blockquote>

              <p className="font-['Inter'] text-white/58 text-base leading-[1.9] mb-5">
                Knight In Shining Armor Heating &amp; Air is dedicated to providing high-quality HVAC services designed for the demands of the Texas climate. Operating out of McKinney, we serve the entire Dallas metro area with a consistent focus on delivering reliable systems, efficient performance, and genuine customer satisfaction.
              </p>
              <p className="font-['Inter'] text-white/58 text-base leading-[1.9] mb-10">
                Every job we take on — from a straightforward AC repair to a full system replacement — is approached with the same unwavering standard. No overselling. No runaround. Just certified, expert HVAC work that keeps your home or business comfortable year-round.
              </p>

              {/* Stats row */}
              <div className="grid grid-cols-2 gap-3">
                {stats.map((stat) => (
                  <div key={stat.value} className="bg-[#071220] border border-white/6 rounded-xl p-5 flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0EA5E9]/10 flex-shrink-0">
                      <i className={`${stat.icon} text-[#0EA5E9] text-base`}></i>
                    </div>
                    <div>
                      <div className="font-['Inter'] text-white font-bold text-sm">{stat.value}</div>
                      <div className="font-['Inter'] text-white/40 text-xs">{stat.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Image with overlap card */}
            <div className="relative mt-6 lg:mt-16">
              <img
                src="https://readdy.ai/api/search-image?query=two%20skilled%20hvac%20technicians%20working%20together%20installing%20high%20efficiency%20air%20conditioning%20unit%20residential%20home%20dallas%20texas%20warm%20afternoon%20daylight%20outdoor%20professional%20attire%20tools%20confident%20teamwork%20premium%20commercial%20photography%20sharp%20detail&width=700&height=580&seq=kisa-about-story-v3&orientation=portrait"
                alt="KISA Cooling Dallas technician team at work"
                className="w-full h-[460px] md:h-[520px] object-cover object-top rounded-2xl"
              />
              {/* Overlay badge */}
              <div className="absolute -bottom-6 -left-5 bg-[#071220] border border-[#0EA5E9]/25 rounded-xl p-5 max-w-[240px] shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0EA5E9]/15 flex-shrink-0">
                    <i className="ri-shield-check-fill text-[#7DD3FC] text-base"></i>
                  </div>
                  <div className="font-['Inter'] text-white font-bold text-sm">Fully Licensed</div>
                </div>
                <p className="font-['Inter'] text-white/48 text-xs leading-relaxed">
                  State-certified &amp; insured for all HVAC work in Texas
                </p>
              </div>

              {/* Decorative rings */}
              <div className="absolute -top-5 -right-5 w-24 h-24 rounded-full border border-[#0EA5E9]/12 pointer-events-none" />
              <div className="absolute -top-3 -right-3 w-14 h-14 rounded-full border border-[#0EA5E9]/18 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-[#030c15] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            {/* Label col */}
            <div>
              <p className="font-['Inter'] text-[#7DD3FC] text-xs font-semibold uppercase tracking-[0.18em] mb-4">Core Values</p>
              <h2 className="font-['Playfair_Display'] text-white text-4xl font-bold leading-tight mb-5">
                What We Stand For
              </h2>
              <p className="font-['Inter'] text-white/50 text-base leading-[1.8]">
                These aren&apos;t slogans — they&apos;re the principles that guide every service call we make across the Dallas area.
              </p>
            </div>
            {/* Values grid — 2 col */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {values.map((v) => (
                <div
                  key={v.title}
                  className={`bg-gradient-to-br ${v.accent} border border-white/6 rounded-2xl p-7 hover:border-[#0EA5E9]/25 transition-all duration-300`}
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0EA5E9]/10 border border-[#0EA5E9]/15 mb-5">
                    <i className={`${v.icon} text-[#7DD3FC] text-xl`}></i>
                  </div>
                  <h3 className="font-['Inter'] text-white font-bold text-base mb-2.5">{v.title}</h3>
                  <p className="font-['Inter'] text-white/50 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <AIAnswerBlock items={aboutQA} title="About Our Company" />
      <MidPageCTA headline="Ready to Work with Dallas&apos; Best?" subtext="Experience the KISA difference — professional HVAC service built on reliability, transparency, and long-term performance." ctaLabel="Get a Free Quote" />
      <ProcessSection />
      <WhyChooseUs />
      <ServiceAreas />
      <TestimonialsSection />
      <FAQSection faqs={aboutFaqs} title="About KISA — FAQs" />
      <FinalCTA
        headline="Ready to Work with Dallas' Best?"
        subheadline="Experience the Knight In Shining Armor difference — professional HVAC service built on reliability, transparency, and long-term performance."
        ctaLabel="Get a Free Quote"
      />
      <Footer />
    </main>
  );
}
